import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguageData } from '../contexts/LanguageContext';
import { getUserInitials, getUserFirstName } from '../utils/userUtils';
import { startSTT, stopSTT } from '../sttClient';
import { speakWithAvatar, stopAvatarSpeech, isAvatarSpeaking } from '../ttsPlayer';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './Learn.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const Learn = ({ user }) => {
  const [selectedChapter, setSelectedChapter] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState(2);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatingMessageIndex, setTranslatingMessageIndex] = useState(null);
  const [translatedText, setTranslatedText] = useState('');
  const [readingMessageIndex, setReadingMessageIndex] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [isReadingTranslation, setIsReadingTranslation] = useState(false);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [chapterPrompts, setChapterPrompts] = useState({});
  const [showChapterDropdown, setShowChapterDropdown] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const conversationIdRef = useRef(null);
  const [showPromptsOverlay, setShowPromptsOverlay] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [sttActive, setSttActive] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const conversationEndRef = useRef(null);
  const loadingIndicatorRef = useRef(null);
  const inputRef = useRef(null);
  const promptsOverlayRef = useRef(null);
  const translationPanelRef = useRef(null);
  const chapterDropdownRef = useRef(null);
  usePageTimeTracker('learn', user);

  // Cleanup STT and TTS on unmount (page navigation)
  useEffect(() => {
    return () => { stopSTT(); stopAvatarSpeech(); window.speechSynthesis.cancel(); };
  }, []);

  // Close chapter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chapterDropdownRef.current && !chapterDropdownRef.current.contains(e.target)) {
        setShowChapterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close prompts overlay when clicking outside
  useEffect(() => {
    if (!showPromptsOverlay) return;
    const handleClickOutside = (e) => {
      if (promptsOverlayRef.current && !promptsOverlayRef.current.contains(e.target)) {
        setShowPromptsOverlay(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPromptsOverlay]);

  // Close translation panel when clicking outside
  useEffect(() => {
    if (!showTranslation) return;
    const handleClickOutside = (e) => {
      if (translationPanelRef.current && !translationPanelRef.current.contains(e.target)) {
        setShowTranslation(false);
        setTranslatingMessageIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslation]);

  const { language: initialLanguage = 'de', proficiencyLevel: initialLevel = 'beginner-a1', resumeChapter: initialResumeChapter = null } = location.state || {};
  const [language, setLanguage] = useState(initialLanguage);
  const [proficiencyLevel, setProficiencyLevel] = useState(initialLevel);
  // When navigated from the Dashboard "Resume Chapter N" CTA, pre-select that chapter
  // and show its prompts once the chapters list loads. Guarded so it only runs once.
  const resumeChapterRef = useRef(initialResumeChapter);
  
  // Use shared language data from context
  const { loading } = useLanguageData();
  
  // Handlers for language and level changes.
  // Changing the language or level mid-session must start a NEW conversation record
  // so the Dashboard tracks each language+level as a separate learning track. The
  // conversation row stores language/level/chapter_id once at creation time, so we
  // reset conversationIdRef here to force a fresh row on the next sent message.
  const handleLanguageChange = (newLanguage) => {
    if (newLanguage !== language) conversationIdRef.current = null;
    setLanguage(newLanguage);
    setSelectedChapter('');
    setShowPrompts(false);
  };

  const handleLevelChange = (newLevel) => {
    if (newLevel !== proficiencyLevel) conversationIdRef.current = null;
    setProficiencyLevel(newLevel);
    setSelectedChapter('');
    setShowPrompts(false);
  };

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Learn', current: true }
  ];

  // Fetch chapters when language and level change
  useEffect(() => {
    const fetchChapters = async () => {
      if (!language || !proficiencyLevel || loading) {
        console.log('Skipping chapter fetch:', { language, proficiencyLevel, loading });
        return;
      }
      
      try {
        // Extract level code from proficiencyLevel (e.g., 'beginner-a1' -> 'A1')
        const levelCode = proficiencyLevel.split('-').pop().toUpperCase();
        
        console.log('Fetching chapters for:', { language, proficiencyLevel, levelCode });
        console.log('API_BASE_URL:', API_BASE_URL);
        console.log('Full API URL:', `${API_BASE_URL}/chapters/${language}/${levelCode}`);
        
        const response = await axios.get(
          `${API_BASE_URL}/chapters/${language}/${levelCode}`,
          { withCredentials: true }
        );
        
        console.log('Chapters API response:', response.data);
        
        // Chapters now include prompts and topics as JSONB
        setChapters(response.data);
        
        // Pre-populate chapterPrompts from the fetched data
        const promptsMap = {};
        response.data.forEach(chapter => {
          if (chapter.prompts && Array.isArray(chapter.prompts)) {
            const initial = chapter.prompts
              .filter(p => p.type === 'initial')
              .sort((a, b) => a.order - b.order)
              .map(p => p.text);
            
            const additional = chapter.prompts
              .filter(p => p.type === 'additional')
              .sort((a, b) => a.order - b.order)
              .map(p => p.text);
            
            promptsMap[chapter.title] = { initial, additional };
          }
        });
        
        setChapterPrompts(promptsMap);
        console.log('Chapters set:', response.data.length, 'chapters');
        console.log('Prompts map:', promptsMap);

        // Resume CTA: pre-select the requested chapter and show its prompts, exactly
        // like a manual chapter selection. Runs once (ref cleared afterwards).
        if (resumeChapterRef.current && response.data.length > 0) {
          const want = parseInt(resumeChapterRef.current, 10);
          const match = response.data.find(c => {
            const m = String(c.title || '').match(/\d+/);
            return m ? parseInt(m[0], 10) === want : false;
          });
          if (match) {
            setSelectedChapter(match.title);
            setShowPrompts(true);
            setShowAllPrompts(false);
          }
          resumeChapterRef.current = null;
        } else if (hasStartedConversation && response.data.length > 0) {
          // Auto-select first chapter when conversation is active (language/level changed mid-conversation)
          setSelectedChapter(response.data[0].title);
          setShowPrompts(false);
        }
      } catch (error) {
        console.error('Error fetching chapters:', error);
        console.error('Error details:', error.response?.data);
        // Fallback to hardcoded chapters with prompts for local development
        const fallbackChapters = [
          { id: 1, title: 'Chapter 1' },
          { id: 2, title: 'Chapter 2' },
          { id: 3, title: 'Chapter 3' },
          { id: 4, title: 'Chapter 4' },
          { id: 5, title: 'Chapter 5' }
        ];
        
        setChapters(fallbackChapters);
        
        // Set dummy prompts for local development
        const dummyPrompts = {
          'Chapter 1': {
            initial: [
              'How do I introduce myself in German?',
              'What are common greetings?',
              'How do I ask someone\'s name?'
            ],
            additional: [
              'How do I say goodbye?',
              'What are formal vs informal greetings?',
              'How do I introduce my family?',
              'What are common phrases for first meetings?'
            ]
          },
          'Chapter 2': {
            initial: [
              'Explain the condizionale passato used for unrealized desires or regrets (e.g., Avrei voluto..., Sarebbe stato meglio...).',
              'Create a complete explanation of the Italian particles ci and ne: their meanings, functions, and common expressions.',
              'This is a very long prompt text to test the ellipsis functionality when the text exceeds the container width and needs to be truncated with three dots at the end'
            ],
            additional: [
              'Teach 15–20 common Italian pronominal verbs (e.g., andarsene, cavarsela, farcela, prendersela).',
              'Explain Italian possessive pronouns (il mio, la tua, i suoi, le nostre, etc.)',
              'The pronominal particles ci and ne.',
              'How do I make reservations?',
              'How do I express dietary restrictions?',
              'What are common Italian greetings?',
              'How do I order coffee in Italy?'
            ]
          },
          'Chapter 3': {
            initial: [
              'How do I ask for directions?',
              'What are common places in a city?',
              'How do I use public transportation?'
            ],
            additional: [
              'How do I buy tickets?',
              'What are common street signs?',
              'How do I describe locations?',
              'How do I ask about distances?'
            ]
          },
          'Chapter 4': {
            initial: [
              'How do I talk about my hobbies?',
              'What are common leisure activities?',
              'How do I make plans with friends?'
            ],
            additional: [
              'How do I talk about sports?',
              'What are cultural activities?',
              'How do I suggest activities?',
              'How do I decline invitations politely?'
            ]
          },
          'Chapter 5': {
            initial: [
              'How do I talk about my job?',
              'What are common professions?',
              'How do I describe my workplace?'
            ],
            additional: [
              'How do I talk about my daily routine?',
              'What are work-related phrases?',
              'How do I schedule meetings?',
              'How do I talk about career goals?'
            ]
          }
        };
        
        setChapterPrompts(dummyPrompts);
        console.log('Using fallback chapters and dummy prompts for local development');
        
        // Auto-select first chapter when conversation is active (language/level changed mid-conversation)
        if (hasStartedConversation && fallbackChapters.length > 0) {
          setSelectedChapter(fallbackChapters[0].title);
          setShowPrompts(false);
        }
      }
    };

    fetchChapters();
  }, [language, proficiencyLevel, loading]);

  // Get prompts based on selected chapter and view state
  const getCurrentPrompts = () => {
    if (!selectedChapter) return [];
    const chapterData = chapterPrompts[selectedChapter];
    if (!chapterData) return [];
    
    // Combine all prompts
    const allPrompts = [...chapterData.initial, ...chapterData.additional];
    
    console.log('getCurrentPrompts - showAllPrompts:', showAllPrompts);
    console.log('getCurrentPrompts - total prompts:', allPrompts.length);
    
    if (showAllPrompts) {
      // Show all prompts when expanded
      console.log('Returning all prompts:', allPrompts.length);
      return allPrompts;
    }
    
    // Show only first 3 prompts initially
    console.log('Returning first 3 prompts');
    return allPrompts.slice(0, 3);
  };

  const hasMorePrompts = () => {
    if (!selectedChapter) return false;
    const chapterData = chapterPrompts[selectedChapter];
    if (!chapterData) return false;
    
    // Check if there are more than 3 prompts total
    const totalPrompts = (chapterData.initial?.length || 0) + (chapterData.additional?.length || 0);
    console.log('hasMorePrompts - total:', totalPrompts, 'has more:', totalPrompts > 3);
    return totalPrompts > 3;
  };

  const prompts = getCurrentPrompts();

  // Auto-scroll logic: scroll to loading indicator when loading, scroll to latest agent response when loaded
  useEffect(() => {
    if (isLoadingResponse && loadingIndicatorRef.current) {
      // When loading, scroll to show the loading indicator (3 dots)
      setTimeout(() => {
        loadingIndicatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isLoadingResponse]);

  // Separate effect for scrolling to new agent messages
  useEffect(() => {
    if (!isLoadingResponse && messages.length > 0 && conversationEndRef.current) {
      // When response loads, scroll to show the start of the latest agent response
      setTimeout(() => {
        conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [messages.length, isLoadingResponse]);

  const handlePromptClick = (prompt) => {
    setPromptText(prompt);
    // Focus and auto-grow
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
      }
    }, 0);
  };

  const handleSendMessage = async (overrideText) => {
    // Capture current text before stopping STT (clearing transcripts triggers useEffect that clears promptText)
    const currentText = overrideText || promptText.trim();

    // Always stop STT if active, regardless of text content
    if (sttActive) {
      stopSTT();
      setSttActive(false);
      setFinalTranscript('');
      setPartialTranscript('');
    }

    if (currentText && !isLoadingResponse) {
      // Auto-select first chapter if none selected
      let activeChapter = selectedChapter;
      if (!activeChapter && chapters.length > 0) {
        activeChapter = chapters[0].title;
        setSelectedChapter(activeChapter);
      }

      // Hide prompts after sending message
      setShowPrompts(false);
      
      // Mark that conversation has started
      setHasStartedConversation(true);
      setShowPromptsOverlay(false);
      
      // Add user message
      const userMessage = currentText;
      setMessages([...messages, { text: userMessage, type: 'user' }]);
      setPromptText('');
      if (inputRef.current) { inputRef.current.style.height = 'auto'; inputRef.current.style.overflowY = 'hidden'; }
      setProgress(2);
      
      // Show loading indicator
      setIsLoadingResponse(true);
      
      // Make API call to get AI response
      try {
        // Find chapter ID from chapters array instead of parsing title string
        // This handles both "Chapter X" and Japanese "N4 Lesson_49" formats
        const chapterObj = chapters.find(c => c.title === activeChapter);
        const chapterMatch = chapterObj?.title?.match(/\d+/);
        const chapterNumber = chapterMatch ? parseInt(chapterMatch[0], 10) : (chapterObj ? chapterObj.id : 1);
        
        // Extract language name and level code from the current values
        const languageNames = {
          'de': 'German',
          'en': 'English',
          'fr': 'French',
          'es': 'Spanish',
          'it': 'Italian',
          'ja': 'Japanese'
        };
        
        const languageName = languageNames[language] || 'German';
        const levelCode = proficiencyLevel ? proficiencyLevel.split('-').pop().toUpperCase() : 'A1';
        
        const response = await axios.post(`${API_BASE_URL}/learn`, {
          "language": languageName,
          "level": levelCode,
          "chapter": chapterNumber,
          "prompt": userMessage
        });
        
        // Parse the response and add to messages
        console.log('API Response:', response.data);
        
        // Extract the answer field - it's nested in content.answer
        let answerText = 'No response';
        
        if (response.data && response.data.content && response.data.content.answer) {
          // The answer is at response.data.content.answer
          answerText = response.data.content.answer;
          console.log('Successfully extracted answer from response.data.content.answer');
        } else if (response.data && response.data.answer) {
          // Fallback: check if answer is directly in response.data
          answerText = response.data.answer;
          console.log('Extracted answer from response.data.answer');
        } else {
          console.error('Could not find answer in response. Response structure:', JSON.stringify(response.data, null, 2));
          answerText = 'Unable to parse response from AI';
        }
        
        console.log('Final answer text:', typeof answerText === 'string' ? answerText.substring(0, 100) + '...' : answerText);
        
        const aiResponse = {
          type: 'agent',
          title: '',
          content: answerText
        };
        
        setMessages(prev => [...prev, aiResponse]);

        // Silently save conversation to DB
        try {
          // Create conversation on first message
          if (!conversationIdRef.current) {
            const convRes = await axios.post(`${API_BASE_URL}/chat/conversation`, {
              enterprise_id: user?.email || 'local-user',
              page_name: 'learn',
              title: userMessage.substring(0, 100),
              language: languageName,
              level: levelCode,
              chapter_id: chapterNumber
            }, { withCredentials: true });
            if (convRes.data?.conversation_id) {
              conversationIdRef.current = convRes.data.conversation_id;
            }
          }
          // Save message pair
          if (conversationIdRef.current) {
            await axios.post(`${API_BASE_URL}/chat/message`, {
              conversation_id: conversationIdRef.current,
              user_input: userMessage,
              agent_response: answerText
            }, { withCredentials: true });
          }
        } catch (saveErr) {
          console.error('Chat save error (non-blocking):', saveErr.message);
        }
      } catch (error) {
        console.error('Error fetching AI response:', error);
        // Show user-friendly error message
        const errorMessage = {
          type: 'agent',
          title: '',
          content: 'There was a problem processing your request. Please try again later or let me know if I can help.'
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        // Hide loading indicator
        setIsLoadingResponse(false);
      }
    }
  };

  const handleTranslate = async (index) => {
    setTranslatingMessageIndex(index);
    setShowTranslation(true);
    setTranslatedText('Translating...');
    
    // Get the text to translate (agent response)
    const textToTranslate = typeof messages[index].content === 'string' 
      ? messages[index].content 
      : JSON.stringify(messages[index].content);
    
    // Map language to full name
    const languageMap = {
      'de': 'German',
      'en': 'English',
      'fr': 'French',
      'es': 'Spanish',
      'it': 'Italian',
      'ja': 'Japanese',
      'german': 'German',
      'english': 'English',
      'french': 'French',
      'spanish': 'Spanish',
      'italian': 'Italian',
      'japanese': 'Japanese'
    };
    
    const sourceLanguage = languageMap[language?.toLowerCase()] || 'German';
    
    try {
      // Call translation API through backend proxy
      const response = await axios.post(`${API_BASE_URL}/translate`, {
        text: textToTranslate,
        source_language: sourceLanguage,
        target_language: 'English',
        level: proficiencyLevel || 'A1',
        context: 'daily routine'
      }, {
        withCredentials: true
      });
      
      if (response.data && response.data.translation) {
        setTranslatedText(response.data.translation);
      } else if (response.data && response.data.translatedText) {
        setTranslatedText(response.data.translatedText);
      } else {
        setTranslatedText('Translation not available');
      }
    } catch (error) {
      console.error('Translation error:', error);
      setTranslatedText('Translation service unavailable. Please try again later.');
    }
  };


  const handleCloseTranslation = () => {
    stopAvatarSpeech();
    setIsReadingTranslation(false);
    setShowTranslation(false);
    setTranslatingMessageIndex(null);
  };

  const handleReadTranslation = async () => {
    if (isReadingTranslation) {
      stopAvatarSpeech();
      setIsReadingTranslation(false);
      return;
    }
    if (!translatedText || translatedText === 'Translating...') return;
    
    // Clean up text for TTS: remove leading numbers in numbered lists
    // e.g., "1 - one\n2 - two" → "one\ntwo" to avoid "one one, two two"
    const cleanedText = translatedText.replace(/^\d+\s*[-–—.)\]]\s*/gm, '');
    
    setIsReadingTranslation(true);
    try {
      await speakWithAvatar(cleanedText, null, 'Joanna');
    } catch (error) {
      console.error('TTS translation error:', error);
    }
  };

  const handleReadAloud = async (index) => {
    // Stop any current speech (including translation reading)
    stopAvatarSpeech();
    setIsReadingTranslation(false);
    window.speechSynthesis.cancel();
    
    if (readingMessageIndex === index && isReading) {
      // If clicking same message while reading, stop
      setIsReading(false);
      setReadingMessageIndex(null);
      return;
    }
    
    const textToRead = typeof messages[index].content === 'string' 
      ? messages[index].content 
      : JSON.stringify(messages[index].content);
    
    // Select voice based on language
    const getVoice = (lang) => {
      const voiceMap = {
        'de': 'Hans', 'german': 'Hans',
        'en': 'Joanna', 'english': 'Joanna',
        'fr': 'Lea', 'french': 'Lea',
        'es': 'Lucia', 'spanish': 'Lucia',
        'it': 'Bianca', 'italian': 'Bianca',
        'ja': 'Mizuki', 'japanese': 'Mizuki'
      };
      return voiceMap[lang?.toLowerCase()] || 'Joanna';
    };

    const selectedVoice = getVoice(language);
    
    setIsReading(true);
    setReadingMessageIndex(index);
    
    try {
      await speakWithAvatar(textToRead, null, selectedVoice);
    } catch (error) {
      console.error('TTS error:', error);
    }
    
    // TTS audio ended callback is handled by ttsPlayer internally
    // We'll reset after a timeout as fallback, or user clicks stop
  };

  // Poll TTS status to reset reading state when audio finishes
  useEffect(() => {
    if (!isReading && !isReadingTranslation) return;
    const checkTTSStatus = setInterval(() => {
      if (!isAvatarSpeaking()) {
        if (isReading) {
          setIsReading(false);
          setReadingMessageIndex(null);
        }
        if (isReadingTranslation) {
          setIsReadingTranslation(false);
        }
      }
    }, 500);
    return () => clearInterval(checkTTSStatus);
  }, [isReading, isReadingTranslation]);

  const handleEdit = (index) => {
    setEditingMessageIndex(index);
    setEditText(messages[index].text);
  };

  const handleSaveEdit = async (index) => {
    if (isLoadingResponse) return; // Prevent duplicate calls
    
    const updatedMessages = [...messages];
    updatedMessages[index].text = editText;
    setMessages(updatedMessages);
    setEditingMessageIndex(null);
    setEditText('');
    
    // Show loading indicator
    setIsLoadingResponse(true);
    
    // Re-send the edited message to get new AI response
    try {
      // Find chapter number from title instead of using DB id
      const chapterObj = chapters.find(c => c.title === selectedChapter);
      const chapterMatch = chapterObj?.title?.match(/\d+/);
      const chapterNumber = chapterMatch ? parseInt(chapterMatch[0], 10) : (chapterObj ? chapterObj.id : 1);
      
      // Extract language name and level code from the current values
      const languageNames = {
        'de': 'German',
        'en': 'English',
        'fr': 'French',
        'es': 'Spanish',
        'it': 'Italian',
        'ja': 'Japanese'
      };
      
      const languageName = languageNames[language] || 'German';
      const levelCode = proficiencyLevel ? proficiencyLevel.split('-').pop().toUpperCase() : 'A1';
      
      const response = await axios.post(`${API_BASE_URL}/learn`, {
        "language": languageName,
        "level": levelCode,
        "chapter": chapterNumber,
        "prompt": editText
      });
      
      console.log('API Response for edited message:', response.data);
      
      // Extract the answer
      let answerText = 'No response';
      
      if (response.data && response.data.content && response.data.content.answer) {
        answerText = response.data.content.answer;
        console.log('Successfully extracted answer from response.data.content.answer');
      } else if (response.data && response.data.answer) {
        answerText = response.data.answer;
        console.log('Extracted answer from response.data.answer');
      } else {
        console.error('Could not find answer in response. Response structure:', JSON.stringify(response.data, null, 2));
        answerText = 'Unable to parse response from AI';
      }
      
      console.log('Final answer text:', typeof answerText === 'string' ? answerText.substring(0, 100) + '...' : answerText);
      
      const aiResponse = {
        type: 'agent',
        title: '',
        content: answerText
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Error fetching AI response for edited message:', error);
    } finally {
      // Hide loading indicator
      setIsLoadingResponse(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setEditText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoadingResponse) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Map language to STT language code
  const getSTTLanguageCode = (lang) => {
    const langMap = {
      'de': 'de-DE',
      'en': 'en-US',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'it': 'it-IT',
      'ja': 'ja-JP',
      'german': 'de-DE',
      'english': 'en-US',
      'french': 'fr-FR',
      'spanish': 'es-ES',
      'italian': 'it-IT',
      'japanese': 'ja-JP'
    };
    return langMap[lang?.toLowerCase()] || 'de-DE';
  };

  // Handle microphone click
  const handleMicClick = async () => {
    if (sttActive) {
      // Stop recording — keep current promptText as-is
      stopSTT();
      setSttActive(false);
      setFinalTranscript('');
      setPartialTranscript('');
    } else {
      // Start recording — keep existing text, append new speech
      setFinalTranscript(promptText);
      setPartialTranscript('');
      setSttActive(true);
      
      // Build WebSocket URL with proper protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/stt`;
      
      const sttLanguage = getSTTLanguageCode(language);
      
      startSTT(
        wsUrl,
        { language: sttLanguage },
        ({ text, isPartial }) => {
          if (isPartial) {
            setPartialTranscript(text);
          } else {
            setFinalTranscript(prev => (prev + ' ' + text).trim());
            setPartialTranscript('');
          }
        }
      );
    }
  };

  // Update promptText when transcript changes
  useEffect(() => {
    if (sttActive) {
      const combinedText = [finalTranscript, partialTranscript].filter(Boolean).join(' ');
      setPromptText(combinedText);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
          inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
        }
      }, 0);
    }
  }, [finalTranscript, partialTranscript, sttActive]);

  // Ref to always have latest values for global Enter handler
  const promptTextRef = useRef(promptText);
  promptTextRef.current = promptText;
  const sttActiveRef = useRef(sttActive);
  sttActiveRef.current = sttActive;
  const handleSendRef = useRef(handleSendMessage);
  handleSendRef.current = handleSendMessage;

  // Global Enter key handler when STT is active (input may not have focus)
  useEffect(() => {
    if (!sttActive) return;
    const handleGlobalEnter = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && sttActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const textToSend = promptTextRef.current?.trim();
        if (textToSend) {
          handleSendRef.current(textToSend);
        }
      }
    };
    document.addEventListener('keydown', handleGlobalEnter, true);
    return () => document.removeEventListener('keydown', handleGlobalEnter, true);
  }, [sttActive]);

  const handleNewChat = () => {
    // Reset all state to start a new conversation
    setMessages([]);
    setPromptText('');
    setShowPrompts(true);
    setShowAllPrompts(false);
    setHasStartedConversation(false);
    setSelectedChapter('');
    setProgress(2);
    setShowTranslation(false);
    setTranslatingMessageIndex(null);
	setShowPromptsOverlay(false);
    setEditingMessageIndex(null);
    setEditText('');
    setIsLoadingResponse(false);
    conversationIdRef.current = null;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="learn-page">
        <Header user={user} />
        <div className="nav-header-container">
          <SideNav />
          <PageHeader 
            breadcrumbs={[{ label: 'Loading...', current: true }]}
            language={language}
            proficiencyLevel={proficiencyLevel}
            onLanguageChange={handleLanguageChange}
            onLevelChange={handleLevelChange}
          />
        </div>
        <main className={`learn-content ${messages.length > 0 ? 'conversation-active' : ''}`}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Loading languages and levels...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`learn-page ${messages.length > 0 ? 'conversation-active' : ''}`}>
      <Header user={user} />
      
      <div className="nav-header-container">
        <SideNav />
        <PageHeader 
          breadcrumbs={breadcrumbs}
          language={language}
          proficiencyLevel={proficiencyLevel}
          onLanguageChange={handleLanguageChange}
          onLevelChange={handleLevelChange}
          showProgress={true}
          progressPercent={progress}
          progressText={progress === 0 ? "Let's begin!" : 'In Progress'}
        />
      </div>
      
      <main className={`learn-content ${messages.length > 0 ? 'has-messages' : ''}`}>
        <div className="learn-page-header">
          <div className="learn-title-row">
            {hasStartedConversation ? (
              <>
                <div className="learn-title-with-chapter">
                  <h1 className="learn-page-main-title">Learn the Language</h1>
                  <div className="chapter-dropdown-inline" ref={chapterDropdownRef}>
                    <span 
                      className="chapter-dropdown-trigger"
                      onClick={() => setShowChapterDropdown(!showChapterDropdown)}
                    >
                      {selectedChapter}
                      <img 
                        src="/images/dropdownarrow.svg"
                        alt="Dropdown"
                        className={`dropdown-icon ${showChapterDropdown ? 'open' : ''}`}
                      />
                    </span>
                    {showChapterDropdown && (
                      <div className="chapter-inline-dropdown-menu">
                        {chapters.map((chapter) => (
                          <div
                            key={chapter.id}
                            className="chapter-inline-option"
                            onClick={() => {
                              // Switching to a different chapter mid-session starts a
                              // new conversation record so the Dashboard tracks the new
                              // chapter_id (progress/current/resume) correctly instead of
                              // merging messages into the previous chapter's row.
                              if (chapter.title !== selectedChapter) conversationIdRef.current = null;
                              setSelectedChapter(chapter.title);
                              setShowChapterDropdown(false);
                            }}
                          >
                            <span>{chapter.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button 
                  className="new-chat-button active"
                  onClick={handleNewChat}
                >
                  New Chat
                </button>
              </>
            ) : (
              <h1 className="learn-page-main-title">Learn the Language</h1>
            )}
          </div>
          <div className="learn-title-line"></div>
        </div>
        
        <div className="conversation-area">
          {!hasStartedConversation && (
            <div className="agent-section">
              <div className="agent-avatar">
                <span>LB</span>
              </div>
              
              <div className="agent-info">
                <div className="welcome-text-purple">
                  Welcome {getUserFirstName(user)}, to the learning session
                </div>
                
                <div className="chapter-selector">
                  <label className="select-label">Select a chapter to start learning</label>
                  <div className="custom-select-wrapper" ref={chapterDropdownRef}>
                    <div 
                      className="custom-select-trigger"
                      onClick={() => setShowChapterDropdown(!showChapterDropdown)}
                    >
                      {selectedChapter ? (
                        <span>{selectedChapter}</span>
                      ) : (
                        <span className="placeholder">Select chapter</span>
                      )}
                      <img 
                        src="/images/dropdownarrow.svg"
                        alt="Dropdown"
                        className={`select-arrow-icon ${showChapterDropdown ? 'open' : ''}`}
                      />
                    </div>
                    {showChapterDropdown && (
                      <div className="custom-select-dropdown">
                        {chapters.map((chapter) => (
                          <div
                            key={chapter.id}
                            className="custom-select-option"
                            onClick={() => {
                              setSelectedChapter(chapter.title);
                              setShowPrompts(true);
                              setShowAllPrompts(false);
                              setShowChapterDropdown(false);
                            }}
                          >
                            <span>{chapter.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedChapter && !showPrompts && (
                    <button 
                      className="show-prompts-link"
                      onClick={() => setShowPrompts(true)}
                    >
                      Show prompts
                    </button>
                  )}
                </div>
              
           
              
              {showPrompts && (
                <div className="prompts-section">
                  <div className="prompts-header">
                    <img src="/images/Group.svg" alt="Prompt icon" />
                    <span>Explore Prompts</span>
                  </div>
                  
                  <div className={`prompt-chips ${showAllPrompts ? 'expanded' : ''}`}>
                    {prompts.map((prompt, index) => (
                      <button 
                        key={index} 
                        className="prompt-chip"
                        onClick={() => handlePromptClick(prompt)}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                  
                  {hasMorePrompts() && (
                    <button 
                      className="view-more-button"
                      onClick={() => setShowAllPrompts(!showAllPrompts)}
                    >
                      <img 
                        src="/images/downarrowicon.png" 
                        alt="Arrow icon" 
                        width="24" 
                        height="24" 
                        style={{transform: showAllPrompts ? 'rotate(180deg)' : 'none'}} 
                      />
                      <span>{showAllPrompts ? 'View less' : 'View more'}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          <div className="messages-scroll-container">
            {messages.map((message, index) => {
              // Find the index of the last agent message
              const lastAgentIndex = messages.map((m, i) => m.type === 'agent' ? i : -1).filter(i => i !== -1).pop();
              const isLastAgentMessage = index === lastAgentIndex;
              
              return (
                <div 
                  key={index} 
                  className={`message-wrapper ${message.type}`}
                  ref={isLastAgentMessage ? conversationEndRef : null}
                >
                  {message.type === 'user' ? (
                    <div className="user-message-container">
                      {editingMessageIndex === index ? (
                        <div className="edit-mode">
                          <textarea 
                            className="edit-textarea"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoFocus
                          />
                          <div className="edit-actions">
                            <button className="save-button" onClick={() => handleSaveEdit(index)}>Save</button>
                            <button className="cancel-button" onClick={handleCancelEdit}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="user-message">
                            <div className="user-avatar-img">{getUserInitials(user)}</div>
                            <p className="message-text">{message.text}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="agent-message-container">
                      <div className="agent-response">
                        <div className="agent-avatar">
                          <span>LB</span>
                        </div>
                        <div className="agent-content">
                          {message.title && <h3 className="agent-response-title">{message.title}</h3>}
                          <div className="agent-response-text">
                            {typeof message.content === 'string' 
                              ? message.content.split('\n').map((line, i) => (
                                  <React.Fragment key={i}>
                                    {line}
                                    {i < message.content.split('\n').length - 1 && <br />}
                                  </React.Fragment>
                                ))
                              : JSON.stringify(message.content, null, 2)
                            }
                          </div>
                        </div>
                      </div>
                      <div className="message-actions agent-actions">
                        <button 
                          className="action-chip" 
                          onClick={() => handleReadAloud(index)}
                        >
                          <img 
                            src={readingMessageIndex === index && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"} 
                            alt={readingMessageIndex === index && isReading ? "Stop" : "Say It Aloud"} 
                            width="16" 
                            height="16" 
                            style={{ marginRight: '4px' }}
                          />
                          <span>{readingMessageIndex === index && isReading ? 'Stop' : 'Say It Aloud'}</span>
                        </button>
                        <button className="action-chip" onClick={() => handleTranslate(index)}>
                          <img src="/images/translateiconactive.svg" alt="translate icon" width="16" height="16" />
                          <span>Translate in English</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {isLoadingResponse && (
              <div className="message-wrapper agent" ref={loadingIndicatorRef}>
                <div className="agent-message-container">
                  <div className="agent-response">
                    <div className="agent-avatar">
                      <span>LB</span>
                    </div>
                    <div className="agent-content">
                      <div className="practice-typing">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="prompt-bar-container">
          <div className="prompt-bar">
            <textarea 
              placeholder="Chat with Buddy" 
              className="prompt-input"
              value={promptText}
              rows={1}
              disabled={isLoadingResponse}
              onChange={(e) => { setPromptText(e.target.value); setShowPromptsOverlay(false); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; e.target.style.overflowY = e.target.scrollHeight > 120 ? 'auto' : 'hidden'; }}
              onKeyDown={handleKeyDown}
              ref={inputRef}
            />
            <div className="prompt-actions">
              {hasStartedConversation && selectedChapter && (
                <button 
                  className="prompt-icon-btn"
                  onClick={() => { if (!isLoadingResponse) setShowPromptsOverlay(!showPromptsOverlay); }}
                  aria-label="Show prompts"
                  style={isLoadingResponse ? {opacity: 0.4, pointerEvents: 'none'} : {}}
                >
                  <img src="/images/Group.svg" alt="Prompts" width="12" height="15" />
				   <span style={{ fontSize: '14px', marginLeft: '4px' }}>Prompts</span>
                </button>
              )}
              <button 
                className={`voice-button ${sttActive ? 'recording' : ''}`}
                aria-label="Voice input"
                onClick={handleMicClick}
                disabled={isLoadingResponse && !sttActive}
                style={isLoadingResponse && !sttActive ? {opacity: 0.4} : {}}
              >
                <img src="/images/micicon.png" alt="Mic icon" width="24" height="24" />
              </button>
              <button 
                className={`send-button ${promptText.trim() ? 'active' : ''}`}
                aria-label="Send message"
                onClick={() => handleSendMessage()}
                disabled={!promptText.trim() || isLoadingResponse}
                style={{opacity: isLoadingResponse ? 0.4 : 1}}
              >
                {promptText.trim() ? (
                  <img src="/images/activeSend.svg" alt="Send icon" width="24" height="24" />
                ) : (
                  <img src="/images/sendicon.png" alt="Send icon" width="24" height="24" />
                )}
              </button>
            </div>
          </div>
          
          {/* Prompts Overlay */}
          {showPromptsOverlay && hasStartedConversation && (
            <div className="prompts-overlay" ref={promptsOverlayRef} style={isLoadingResponse ? {opacity: 0.5, pointerEvents: 'none'} : {}}>
              <div className="prompts-overlay-header">
                <img src="/images/Group.svg" alt="Prompts" width="12" height="15" />
                <span>Explore prompt library</span>
              </div>
              <div className="prompts-overlay-content">
                {(() => {
                  const chapterData = chapterPrompts[selectedChapter];
                  if (!chapterData) return null;
                  const allPrompts = [...(chapterData.initial || []), ...(chapterData.additional || [])];
                  return allPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      className="prompts-overlay-chip"
                      onClick={() => {
                        handlePromptClick(prompt);
                        setShowPromptsOverlay(false);
                      }}
                    >
                      {prompt}
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}
          
          <div className="disclaimer">
            <p>
              Response generated by AI. Verify accuracy and privacy restrictions before use. </p> <div className="discalimerlinks"><button className="disclaimer-link" onClick={() => setShowTermsModal(true)}>Terms of use</button> <button className="disclaimer-link" onClick={() => setShowPrivacyModal(true)}>Privacy policy</button></div>
            
          </div>
        </div>
      </main>
      
      {showTranslation && translatingMessageIndex !== null && (
        <div className="translation-panel" ref={translationPanelRef}>
          <div className="translation-header">
            <h3>Translation</h3>
            <button className="close-button" onClick={handleCloseTranslation}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#000000"/>
              </svg>
            </button>
          </div>
          
          <div className="translation-content">
            <div className="translation-section">
              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                <h4 style={{margin: 0}}>English Translation</h4>
                <button 
                  className="action-chip" 
                  onClick={handleReadTranslation}
                  disabled={!translatedText || translatedText === 'Translating...'}
                  style={{opacity: (!translatedText || translatedText === 'Translating...') ? 0.4 : 1}}
                >
                  <img 
                    src={isReadingTranslation ? "/images/muteicon.svg" : "/images/readicon.svg"} 
                    alt={isReadingTranslation ? "Stop" : "Say It Aloud"} 
                    width="16" 
                    height="16" 
                    style={{ marginRight: '4px' }}
                  />
                  <span>{isReadingTranslation ? 'Stop' : 'Say It Aloud'}</span>
                </button>
              </div>
              <div style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>{translatedText}</div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />

      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="footer-privacy-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Privacy Policy</h2>
                <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Data Privacy Statement</h3>
                <p>Please be aware that any personal data that you and others provide through this tool may be processed by Accenture.</p>
                <p>The protection of your personal data is very important to Accenture. Accenture is committed to keeping your personal data secure, and processing it in accordance with applicable data protection laws and our internal policies including our <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">Global Data Privacy Policy</a>.</p>
                <p>Before providing any personal data through this tool, Accenture invites you to read its <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">privacy statement</a>, which includes important information on why and how Accenture is processing your personal data.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowPrivacyModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="footer-term-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Terms of Use</h2>
                <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Acceptance of Terms</h3>
                <p>By accessing and using Language Buddy, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.</p>
                <h3>License and Access</h3>
                <p>Language Buddy grants you a limited, non-exclusive, non-transferable license to access and use the platform for personal language learning purposes.</p>
                <h3>User Conduct</h3>
                <p>You agree to use Language Buddy in accordance with all applicable laws and regulations.</p>
                <h3>Intellectual Property</h3>
                <p>All content, materials, features, and functionality available on Language Buddy are the property of Accenture and are protected by copyright, trademark, and other intellectual property laws.</p>
                <h3>Disclaimer of Warranties</h3>
                <p>Language Buddy is provided "as is" without warranties of any kind, either express or implied.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowTermsModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Learn;
